import { motion } from 'framer-motion';
import { useState } from 'react';

interface Testimonial {
  id: number;
  name: string;
  company: string;
  role: string;
  content: string;
  rating: number;
  image: string;
  destination: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Amadou Diallo',
    company: 'Import-Export Diallo & Fils',
    role: 'Directeur Général',
    content: 'ATLAS Transport a révolutionné notre chaîne logistique. Leurs services de groupage vers Conakry sont impeccables. Délais respectés, marchandises toujours en parfait état. Une équipe professionnelle et réactive qui comprend vraiment les enjeux du commerce avec l\'Afrique.',
    rating: 5,
    image: 'https://public.readdy.ai/ai/img_res/a610d11a59fca8e7c8a8c56ce21496ef.jpg',
    destination: 'Conakry, Guinée'
  },
  {
    id: 2,
    name: 'Sophie Mensah',
    company: 'Boutique Afro-Chic',
    role: 'Fondatrice',
    content: 'Je travaille avec ATLAS depuis 3 ans pour mes envois vers Accra et Lomé. Leur service de stockage est parfait pour gérer mes stocks avant expédition. L\'équipe est toujours disponible et de bon conseil. Je recommande vivement leurs services !',
    rating: 5,
    image: 'https://readdy.ai/api/search-image?query=elegant%20african%20businesswoman%20entrepreneur%20smiling%20warmly%20in%20stylish%20modern%20office%2C%20professional%20portrait%20with%20natural%20daylight%2C%20confident%20and%20approachable%20demeanor%2C%20contemporary%20business%20environment%20with%20soft%20colors&width=400&height=400&seq=testimonial-sophie-002&orientation=squarish',
    destination: 'Accra & Lomé'
  },
  {
    id: 3,
    name: 'Ibrahim Touré',
    company: 'Électronique Moderne SARL',
    role: 'Responsable Logistique',
    content: 'Transport aérien rapide et sécurisé pour nos équipements électroniques vers Abidjan. ATLAS nous a permis de réduire nos délais de livraison de 40%. Leur suivi en temps réel et leur transparence sur les coûts font toute la différence. Partenaire de confiance !',
    rating: 5,
    image: 'https://readdy.ai/api/search-image?query=professional%20african%20logistics%20manager%20in%20business%20casual%20attire%20smiling%20in%20modern%20warehouse%20office%2C%20authentic%20portrait%20style%20with%20natural%20lighting%2C%20organized%20workspace%20background%2C%20confident%20and%20friendly%20expression&width=400&height=400&seq=testimonial-ibrahim-003&orientation=squarish',
    destination: 'Abidjan, Côte d\'Ivoire'
  },
  {
    id: 4,
    name: 'Fatou Ndiaye',
    company: 'Cosmétiques Naturels Sahel',
    role: 'Directrice Commerciale',
    content: 'Excellente expérience avec ATLAS pour nos expéditions maritimes vers Dakar. Leurs tarifs sont compétitifs et le service client est exceptionnel. Ils ont géré toutes les formalités douanières avec professionnalisme. Je ne changerais pour rien au monde !',
    rating: 5,
    image: 'https://readdy.ai/api/search-image?query=elegant%20african%20female%20executive%20in%20professional%20business%20attire%20smiling%20warmly%2C%20natural%20portrait%20photography%20in%20bright%20modern%20office%2C%20confident%20and%20sophisticated%20appearance%2C%20contemporary%20workspace%20with%20soft%20natural%20light&width=400&height=400&seq=testimonial-fatou-004&orientation=squarish',
    destination: 'Dakar, Sénégal'
  },
  {
    id: 5,
    name: 'Kofi Asante',
    company: 'Matériaux de Construction KA',
    role: 'Gérant',
    content: 'Pour nos envois de matériaux vers Bamako et Ouagadougou, ATLAS est notre partenaire privilégié. Leur expertise en groupage nous permet d\'optimiser nos coûts. Service fiable, équipe compétente, suivi impeccable. Cinq étoiles méritées !',
    rating: 5,
    image: 'https://readdy.ai/api/search-image?query=professional%20african%20businessman%20in%20smart%20casual%20business%20wear%20smiling%20confidently%2C%20authentic%20portrait%20in%20modern%20industrial%20office%20setting%2C%20natural%20daylight%20photography%2C%20approachable%20and%20trustworthy%20demeanor&width=400&height=400&seq=testimonial-kofi-005&orientation=squarish',
    destination: 'Bamako & Ouagadougou'
  },
  {
    id: 6,
    name: 'Marie-Claire Koffi',
    company: 'Textile & Mode Africaine',
    role: 'Présidente',
    content: 'ATLAS comprend parfaitement les besoins spécifiques du secteur textile. Leurs solutions de stockage et leur service de transport maritime vers Cotonou sont exactement ce dont nous avions besoin. Professionnalisme, ponctualité et tarifs justes. Bravo !',
    rating: 5,
    image: 'https://readdy.ai/api/search-image?query=sophisticated%20african%20businesswoman%20fashion%20entrepreneur%20smiling%20elegantly%20in%20chic%20modern%20office%2C%20professional%20portrait%20with%20natural%20lighting%2C%20stylish%20and%20confident%20appearance%2C%20contemporary%20workspace%20with%20refined%20aesthetic&width=400&height=400&seq=testimonial-marie-006&orientation=squarish',
    destination: 'Cotonou, Bénin'
  }
];

const TestimonialsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <i
        key={index}
        className={`ri-star-${index < rating ? 'fill' : 'line'} text-yellow-400`}
      ></i>
    ));
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Témoignages Clients
          </span>
          <h2 className="font-serif text-4xl lg:text-5xl font-bold text-primary mb-4">
            Ils Nous Font Confiance
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Découvrez les expériences de nos clients satisfaits à travers l'Afrique
          </p>
        </motion.div>

        <div className="relative">
          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl shadow-xl p-8 lg:p-12"
          >
            <div className="flex flex-col lg:flex-row gap-8 items-center">
              <div className="flex-shrink-0">
                <div className="relative">
                  <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full overflow-hidden border-4 border-accent/20">
                    <img
                      src={testimonials[activeIndex].image}
                      alt={testimonials[activeIndex].name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-2 -right-2 bg-accent text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg">
                    <i className="ri-double-quotes-r text-2xl"></i>
                  </div>
                </div>
              </div>

              <div className="flex-1 text-center lg:text-left">
                <div className="flex justify-center lg:justify-start gap-1 mb-4">
                  {renderStars(testimonials[activeIndex].rating)}
                </div>

                <p className="text-gray-700 text-lg leading-relaxed mb-6 italic">
                  "{testimonials[activeIndex].content}"
                </p>

                <div className="space-y-2">
                  <h4 className="font-bold text-xl text-primary">
                    {testimonials[activeIndex].name}
                  </h4>
                  <p className="text-accent font-semibold">
                    {testimonials[activeIndex].role}
                  </p>
                  <p className="text-gray-600">
                    {testimonials[activeIndex].company}
                  </p>
                  <div className="flex items-center justify-center lg:justify-start gap-2 text-sm text-gray-500 mt-3">
                    <i className="ri-map-pin-line text-accent"></i>
                    <span>{testimonials[activeIndex].destination}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <button
            onClick={prevTestimonial}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-6 bg-white hover:bg-accent text-primary hover:text-white w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all cursor-pointer z-10"
            aria-label="Témoignage précédent"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>

          <button
            onClick={nextTestimonial}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-6 bg-white hover:bg-accent text-primary hover:text-white w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all cursor-pointer z-10"
            aria-label="Témoignage suivant"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
        </div>

        <div className="flex justify-center gap-2 mt-8">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`w-3 h-3 rounded-full transition-all cursor-pointer ${
                index === activeIndex
                  ? 'bg-accent w-8'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Aller au témoignage ${index + 1}`}
            ></button>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-8"
        >
          <div className="text-center">
            <div className="text-4xl font-bold text-accent mb-2">500+</div>
            <div className="text-gray-600 text-sm">Clients Satisfaits</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-accent mb-2">15+</div>
            <div className="text-gray-600 text-sm">Pays Desservis</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-accent mb-2">98%</div>
            <div className="text-gray-600 text-sm">Taux de Satisfaction</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-accent mb-2">10K+</div>
            <div className="text-gray-600 text-sm">Envois Réussis</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
